// utils/deepLinkService.js
// ===== CENTRALIZED DEEP LINK SERVICE =====
// Single parser and navigator for all incoming deep links

import { CANONICAL_HOST, CUSTOM_SCHEME, LINK_TYPES } from './deepLinkConfig';
import { safeStorage } from './safeStorage';
import { registerOnce } from './registerOnce';

// Storage key for auth resume
const PENDING_DEEP_LINK_KEY = 'gweffit_pending_deep_link';

/**
 * Parse an incoming URL (Universal Link, App Link, or custom scheme)
 * into a structured object for routing.
 *
 * @param {string} url - The incoming URL to parse
 * @returns {object|null} Parsed link info or null if invalid
 *
 * @example
 * parseIncomingUrl('https://gweffit.com/t/abc123')
 * → { type: 'trial', id: 'abc123', route: '/trial/abc123', params: {}, raw: '...' }
 *
 * parseIncomingUrl('gweffit://t/xyz')
 * → { type: 'trial', id: 'xyz', route: '/trial/xyz', params: {}, raw: '...' }
 */
export function parseIncomingUrl(url) {
  if (!url || typeof url !== 'string') {
    console.warn('[DeepLink] Invalid URL provided:', url);
    return null;
  }

  try {
    let pathname = '';
    let searchParams = new URLSearchParams();
    let method = 'unknown';

    // Handle custom scheme URLs (gweffit://...)
    if (url.startsWith(`${CUSTOM_SCHEME}://`)) {
      method = 'scheme';
      // Parse as if it were https to use URL API
      const fakeUrl = url.replace(`${CUSTOM_SCHEME}://`, 'https://www.gweffit.com/');
      const parsed = new URL(fakeUrl);
      pathname = parsed.pathname;
      searchParams = parsed.searchParams;
    }
    // Handle https URLs
    else if (url.startsWith('https://') || url.startsWith('http://')) {
      const parsed = new URL(url);

      // Verify it's from our canonical host (or allow localhost for dev)
      const isCanonical = parsed.host === 'gweffit.com' || parsed.host === 'www.gweffit.com';
      const isLocalDev = parsed.host === 'localhost' || parsed.host.includes('localhost:');

      if (!isCanonical && !isLocalDev) {
        console.warn('[DeepLink] URL is not from canonical host:', url);
        // Still try to parse it, but note it's not canonical
      }

      method = isCanonical ? 'universal_link' : 'web_only';
      pathname = parsed.pathname;
      searchParams = parsed.searchParams;
    }
    // Handle relative paths (for internal routing)
    else if (url.startsWith('/')) {
      method = 'internal';
      const parsed = new URL(url, CANONICAL_HOST);
      pathname = parsed.pathname;
      searchParams = parsed.searchParams;
    }
    else {
      console.warn('[DeepLink] Unrecognized URL format:', url);
      return null;
    }

    // Normalize pathname (remove trailing slash, lowercase)
    pathname = pathname.replace(/\/$/, '').toLowerCase();

    // Try to match against known link types
    for (const [typeName, typeConfig] of Object.entries(LINK_TYPES)) {
      // Check primary path
      const primaryPattern = `${typeConfig.webPath}/`;
      if (pathname.startsWith(primaryPattern) || pathname === typeConfig.webPath) {
        const id = pathname.substring(primaryPattern.length) || searchParams.get(typeConfig.paramName);
        if (id) {
          return {
            type: typeName,
            id: decodeURIComponent(id),
            route: getAppRoute(typeName, id),
            params: Object.fromEntries(searchParams),
            requiresAuth: typeConfig.requiresAuth,
            method,
            raw: url,
          };
        }
      }

      // Check legacy paths
      for (const legacyPath of typeConfig.legacyPaths || []) {
        const legacyPattern = `${legacyPath}/`;
        if (pathname.startsWith(legacyPattern) || pathname === legacyPath) {
          const id = pathname.substring(legacyPattern.length) || searchParams.get(typeConfig.paramName);
          if (id) {
            return {
              type: typeName,
              id: decodeURIComponent(id),
              route: getAppRoute(typeName, id),
              params: Object.fromEntries(searchParams),
              requiresAuth: typeConfig.requiresAuth,
              method,
              raw: url,
            };
          }
        }
      }
    }

    // No match found - return generic parse
    console.log('[DeepLink] No specific handler for path:', pathname);
    return {
      type: 'unknown',
      id: null,
      route: pathname,
      params: Object.fromEntries(searchParams),
      requiresAuth: false,
      method,
      raw: url,
    };

  } catch (err) {
    console.error('[DeepLink] Failed to parse URL:', url, err);
    return null;
  }
}

/**
 * Get the in-app route for a given link type and ID
 */
function getAppRoute(type, id) {
  switch (type) {
    case 'trial':
      return `/t/${id}`; // Canonical short URL
    case 'summons':
      return `/summons/${id}`;
    case 'crew':
      return `/c/${id}`; // Canonical short URL
    case 'profile':
      return `/u/${id}`; // Canonical short URL
    case 'invite':
      return `/invite/${id}`;
    default:
      return '/';
  }
}

/**
 * Navigate to a parsed deep link using the app's router.
 * Handles auth gating and stores pending navigation if needed.
 *
 * @param {object} parsed - Result from parseIncomingUrl
 * @param {function} navigate - React Router navigate function
 * @param {object} options - { isAuthenticated, onAuthRequired }
 */
export function navigateToParsedLink(parsed, navigate, options = {}) {
  const { isAuthenticated = false, onAuthRequired = null } = options;

  if (!parsed || !parsed.route) {
    console.warn('[DeepLink] Cannot navigate - invalid parsed link:', parsed);
    return false;
  }

  // Track the deep link open
  trackDeepLinkOpen(parsed);

  // Check if auth is required
  if (parsed.requiresAuth && !isAuthenticated) {
    console.log('[DeepLink] Auth required, storing pending navigation:', parsed.route);
    storePendingDeepLink(parsed);

    if (onAuthRequired) {
      onAuthRequired(parsed);
    }
    return false;
  }

  // Navigate to the route
  try {
    console.log('[DeepLink] Navigating to:', parsed.route);
    navigate(parsed.route, {
      state: {
        fromDeepLink: true,
        deepLinkParams: parsed.params,
        deepLinkType: parsed.type,
      }
    });
    return true;
  } catch (err) {
    console.error('[DeepLink] Navigation failed:', err);
    return false;
  }
}

/**
 * Store a pending deep link for after auth
 */
export function storePendingDeepLink(parsed) {
  try {
    safeStorage.set(PENDING_DEEP_LINK_KEY, JSON.stringify({
      ...parsed,
      storedAt: Date.now(),
    }));
  } catch (err) {
    console.error('[DeepLink] Failed to store pending link:', err);
  }
}

/**
 * Get and clear a pending deep link (call after successful auth)
 */
export function consumePendingDeepLink() {
  try {
    const stored = safeStorage.get(PENDING_DEEP_LINK_KEY);
    if (!stored) return null;

    safeStorage.remove(PENDING_DEEP_LINK_KEY);

    const parsed = JSON.parse(stored);

    // Check if the stored link is too old (24 hours)
    if (parsed.storedAt && Date.now() - parsed.storedAt > 24 * 60 * 60 * 1000) {
      console.log('[DeepLink] Pending link expired');
      return null;
    }

    return parsed;
  } catch (err) {
    console.error('[DeepLink] Failed to consume pending link:', err);
    return null;
  }
}

/**
 * Check if there's a pending deep link
 */
export function hasPendingDeepLink() {
  try {
    return !!safeStorage.get(PENDING_DEEP_LINK_KEY);
  } catch {
    return false;
  }
}

/**
 * Track deep link open event (observability)
 * Uses window.trackEvent if available (defined in App.js)
 */
function trackDeepLinkOpen(parsed) {
  try {
    const eventData = {
      url: parsed.raw,
      type: parsed.type,
      id: parsed.id,
      method: parsed.method,
      platform: getPlatform(),
      source: getDeepLinkSource(),
    };

    // Use existing trackEvent if available
    if (typeof window !== 'undefined' && window.trackEvent) {
      window.trackEvent('deep_link_opened', eventData);
    } else {
      // Fallback to console in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[DeepLink] Event:', 'deep_link_opened', eventData);
      }
    }
  } catch (err) {
    // Don't let tracking errors break navigation
    console.warn('[DeepLink] Tracking failed:', err);
  }
}

/**
 * Detect platform
 */
function getPlatform() {
  if (typeof window === 'undefined') return 'server';

  const ua = navigator.userAgent || '';

  // Check if running in Capacitor native app
  if (window.Capacitor?.isNativePlatform?.()) {
    return window.Capacitor.getPlatform?.() || 'native';
  }

  if (/iPad|iPhone|iPod/.test(ua)) return 'ios_web';
  if (/Android/.test(ua)) return 'android_web';
  if (/Mac/.test(ua)) return 'mac_web';
  if (/Win/.test(ua)) return 'windows_web';

  return 'web';
}

/**
 * Try to detect the source of the deep link
 */
function getDeepLinkSource() {
  if (typeof document === 'undefined') return 'unknown';

  const referrer = document.referrer || '';

  if (referrer.includes('twitter.com') || referrer.includes('t.co')) return 'twitter';
  if (referrer.includes('facebook.com') || referrer.includes('fb.com')) return 'facebook';
  if (referrer.includes('instagram.com')) return 'instagram';
  if (referrer.includes('reddit.com')) return 'reddit';
  if (referrer.includes('tiktok.com')) return 'tiktok';
  if (referrer.includes('linkedin.com')) return 'linkedin';
  if (referrer.includes('messages') || referrer.includes('sms')) return 'sms';
  if (referrer.includes('mail') || referrer.includes('gmail')) return 'email';
  if (!referrer) return 'direct';

  return 'other';
}

/**
 * Initialize deep link handling for Capacitor apps.
 * Call this once at app startup.
 *
 * @param {function} navigate - React Router navigate function
 * @param {function} getAuthState - Function that returns { isAuthenticated }
 * @param {function} onAuthRequired - Called when deep link requires auth
 */
export async function initDeepLinkHandler(navigate, getAuthState, onAuthRequired) {
  if (typeof window === 'undefined') return;

  const handleCustomUrlOpen = (event) => {
    const detail = event?.detail || {};
    const url = detail?.url;
    const source = detail?.source || 'custom';
    if (!url) return;
    if (source === 'appUrlOpen' || source === 'getLaunchUrl') return;

    console.log('[DeepLink] Custom URL event received:', { url, source });
    const parsed = parseIncomingUrl(url);
    if (!parsed) return;
    const authState = getAuthState();
    navigateToParsedLink(parsed, navigate, {
      isAuthenticated: authState?.isAuthenticated || false,
      onAuthRequired,
    });
  };

  window.addEventListener('gweffit:urlopen', handleCustomUrlOpen);

  // Only set up native handlers if running in Capacitor
  if (!window.Capacitor?.isNativePlatform?.()) {
    console.log('[DeepLink] Not in native app, skipping Capacitor handlers');
    return () => window.removeEventListener('gweffit:urlopen', handleCustomUrlOpen);
  }

  try {
    // Dynamic import to avoid issues on web
    const { App } = await import('@capacitor/app');

    // Handle warm start (app already running, opened via link)
    await registerOnce('cap_deeplink_appUrlOpen', async () => {
      const handle = await App.addListener('appUrlOpen', ({ url }) => {
        console.log('[DeepLink] App opened with URL:', url);
        window.dispatchEvent(new CustomEvent('gweffit:urlopen', { detail: { url, source: 'appUrlOpen' } }));

        const parsed = parseIncomingUrl(url);
        if (parsed) {
          const justJoined = safeStorage.get('crewJoinJustJoined');
          if (
            parsed.type === 'crew' &&
            justJoined &&
            parsed.id &&
            justJoined.toLowerCase() === parsed.id.toLowerCase()
          ) {
            console.log('[DeepLink] Crew just joined; ignoring deep link and redirecting to profile');
            safeStorage.remove('crewJoinJustJoined');
            navigate('/profile', { replace: true });
            return;
          }
          const authState = getAuthState();
          navigateToParsedLink(parsed, navigate, {
            isAuthenticated: authState?.isAuthenticated || false,
            onAuthRequired,
          });
        }
      });
      return handle;
    });

    // Handle cold start (app launched via link)
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      console.log('[DeepLink] App launched with URL:', launchUrl.url);
      window.dispatchEvent(new CustomEvent('gweffit:urlopen', { detail: { url: launchUrl.url, source: 'getLaunchUrl' } }));

      const parsed = parseIncomingUrl(launchUrl.url);
      if (parsed) {
        const justJoined = safeStorage.get('crewJoinJustJoined');
        if (
          parsed.type === 'crew' &&
          justJoined &&
          parsed.id &&
          justJoined.toLowerCase() === parsed.id.toLowerCase()
        ) {
          console.log('[DeepLink] Crew just joined; ignoring launch deep link and redirecting to profile');
          safeStorage.remove('crewJoinJustJoined');
          navigate('/profile', { replace: true });
          return;
        }
        const authState = getAuthState();
        navigateToParsedLink(parsed, navigate, {
          isAuthenticated: authState?.isAuthenticated || false,
          onAuthRequired,
        });
      }
    }

    console.log('[DeepLink] Native handlers initialized');

  } catch (err) {
    console.error('[DeepLink] Failed to initialize native handlers:', err);
  }

  return () => window.removeEventListener('gweffit:urlopen', handleCustomUrlOpen);
}

/**
 * Handle web-based deep links on initial page load.
 * Call this in App.js useEffect.
 *
 * @param {function} navigate - React Router navigate function
 * @param {object} location - React Router location object
 */
export function handleWebDeepLink(navigate, location) {
  if (typeof window === 'undefined') return;

  // Don't handle if we're in a native app (Capacitor handles it)
  if (window.Capacitor?.isNativePlatform?.()) return;

  const fullUrl = window.location.href;
  const parsed = parseIncomingUrl(fullUrl);

  if (parsed && parsed.type !== 'unknown') {
    console.log('[DeepLink] Web deep link detected:', parsed);
    trackDeepLinkOpen(parsed);
    // Navigation happens via React Router automatically for web
  }
}

const deepLinkService = {
  parseIncomingUrl,
  navigateToParsedLink,
  storePendingDeepLink,
  consumePendingDeepLink,
  hasPendingDeepLink,
  initDeepLinkHandler,
  handleWebDeepLink,
};

export default deepLinkService;
